import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import classes from "./charts.module.css";
import { useSelector } from "react-redux";
import { useMerchantInvoiceDashboardOffline } from "../../../../offline";
import {
  ALL_MONTHS,
  formatCompactNumber,
  getAvailableYears,
  getDaysInMonth,
  getMonthLabel,
  MONTH_LONG_NAMES,
  MONTH_SHORT_NAMES,
  toDateValue,
  toSafeNumber,
} from "./chartPeriodUtils";

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const VendorChart = () => {
  const Data = useSelector((state) => state.loginData.value);
  const auth_token = Data?.auth_token;
  const user_token = Data?.user?.token || Data?.user_token;
  const org_id = Data?.org_id || Data?.user?.org_id || Data?.organization_id;
  const { data: dashboardSummary } = useMerchantInvoiceDashboardOffline(
    auth_token,
    user_token,
    org_id
  );

  const trend = dashboardSummary?.vendor_onboard_trend || {};

  const initiatedEntries = useMemo(
    () =>
      (trend?.initiated || [])
        .map((item) => {
          const date = toDateValue(item?.date);
          return date
            ? {
                date,
                count: toSafeNumber(item?.count),
              }
            : null;
        })
        .filter(Boolean),
    [trend]
  );

  const onboardedEntries = useMemo(
    () =>
      (trend?.onboarded || [])
        .map((item) => {
          const date = toDateValue(item?.date);
          return date
            ? {
                date,
                count: toSafeNumber(item?.count),
              }
            : null;
        })
        .filter(Boolean),
    [trend]
  );

  const availableYears = useMemo(() => {
    const years = getAvailableYears([
      ...initiatedEntries.map((item) => item.date),
      ...onboardedEntries.map((item) => item.date),
    ]);

    return years.length ? years : [new Date().getFullYear()];
  }, [initiatedEntries, onboardedEntries]);

  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(ALL_MONTHS);

  useEffect(() => {
    const fallbackYear = String(availableYears[0] || "");
    if (!selectedYear || !availableYears.includes(Number(selectedYear))) {
      setSelectedYear(fallbackYear);
      setSelectedMonth(ALL_MONTHS);
    }
  }, [availableYears, selectedYear]);

  const resolvedYear = Number(selectedYear || availableYears[0] || new Date().getFullYear());
  const resolvedMonth = selectedMonth === ALL_MONTHS ? ALL_MONTHS : Number(selectedMonth);

  const activeSeries = useMemo(() => {
    if (!Number.isFinite(resolvedYear)) {
      return [];
    }

    if (resolvedMonth !== ALL_MONTHS) {
      const daysInMonth = getDaysInMonth(resolvedYear, resolvedMonth);

      const initiatedMap = new Map(
        initiatedEntries
          .filter(
            (entry) =>
              entry.date.getFullYear() === resolvedYear &&
              entry.date.getMonth() === resolvedMonth
          )
          .map((entry) => [entry.date.getDate(), toSafeNumber(entry.count)])
      );

      const onboardedMap = new Map(
        onboardedEntries
          .filter(
            (entry) =>
              entry.date.getFullYear() === resolvedYear &&
              entry.date.getMonth() === resolvedMonth
          )
          .map((entry) => [entry.date.getDate(), toSafeNumber(entry.count)])
      );

      return Array.from({ length: daysInMonth }, (_, index) => {
        const dayNumber = index + 1;
        return {
          label: `${dayNumber}`,
          initiated: initiatedMap.get(dayNumber) || 0,
          onboarded: onboardedMap.get(dayNumber) || 0,
        };
      });
    }

    const monthlySeries = Array.from({ length: 12 }, (_, monthIndex) => ({
      label: MONTH_SHORT_NAMES[monthIndex],
      initiated: 0,
      onboarded: 0,
    }));

    initiatedEntries
      .filter((entry) => entry.date.getFullYear() === resolvedYear)
      .forEach((entry) => {
        const monthIndex = entry.date.getMonth();
        monthlySeries[monthIndex].initiated += toSafeNumber(entry.count);
      });

    onboardedEntries
      .filter((entry) => entry.date.getFullYear() === resolvedYear)
      .forEach((entry) => {
        const monthIndex = entry.date.getMonth();
        monthlySeries[monthIndex].onboarded += toSafeNumber(entry.count);
      });

    return monthlySeries;
  }, [initiatedEntries, onboardedEntries, resolvedMonth, resolvedYear]);

  const totalInitiated = activeSeries.reduce(
    (sum, item) => sum + toSafeNumber(item?.initiated),
    0
  );
  const totalOnboarded = activeSeries.reduce(
    (sum, item) => sum + toSafeNumber(item?.onboarded),
    0
  );

  const conversionRate = totalInitiated
    ? Math.round((totalOnboarded / totalInitiated) * 100)
    : 0;

  const strongestWindow = activeSeries.reduce(
    (best, item) =>
      toSafeNumber(item?.onboarded) > toSafeNumber(best?.onboarded) ? item : best,
    activeSeries[0] || { label: "-", onboarded: 0 }
  );

  const chartHeading = resolvedMonth === ALL_MONTHS
    ? `Monthly onboarding flow for ${resolvedYear}`
    : `Daily onboarding flow for ${getMonthLabel(resolvedMonth)} ${resolvedYear}`;

  const chartData = {
    labels: activeSeries.map((item) => item.label),
    datasets: [
      {
        type: "bar",
        label: "Initiated",
        data: activeSeries.map((item) => item.initiated),
        backgroundColor: "rgba(14, 116, 144, 0.78)",
        borderRadius: 10,
        borderSkipped: false,
        maxBarThickness: 26,
      },
      {
        type: "line",
        label: "Onboarded",
        data: activeSeries.map((item) => item.onboarded),
        borderColor: "#16a34a",
        backgroundColor: "rgba(22, 163, 74, 0.18)",
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#16a34a",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 3,
        tension: 0.34,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(9, 20, 42, 0.94)",
        borderColor: "rgba(120, 168, 255, 0.24)",
        borderWidth: 1,
        padding: 14,
        titleColor: "#dbeafe",
        bodyColor: "#ffffff",
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${formatCompactNumber(context.parsed?.y || 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#47607f",
          font: {
            family: "gilroyMedium",
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawBorder: false,
        },
        ticks: {
          color: "#47607f",
          precision: 0,
          font: {
            family: "gilroyMedium",
            size: 11,
          },
          callback: (value) => formatCompactNumber(value),
        },
      },
    },
  };

  return (
    <section className={classes.invoiceChartWrapper}>
      <div className={classes.chartHeader}>
        <div>
          <h2 className={classes.chartTitle}>Merchant Onboarding</h2>
          <p className={classes.chartSubtitle}>{chartHeading}</p>
        </div>

        <div className={classes.chartFilters}>
          <label className={classes.chartFilterField}>
            <span className={classes.chartFilterLabel}>Year</span>
            <select
              value={selectedYear}
              onChange={(event) => {
                setSelectedYear(event.target.value);
                setSelectedMonth(ALL_MONTHS);
              }}
              className={classes.chartFilterSelect}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>

          <label className={classes.chartFilterField}>
            <span className={classes.chartFilterLabel}>Month</span>
            <select
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              className={classes.chartFilterSelect}
            >
              <option value={ALL_MONTHS}>All Months</option>
              {MONTH_LONG_NAMES.map((monthName, index) => (
                <option key={monthName} value={index}>
                  {monthName}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className={classes.chartInsightStrip}>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Initiated</span>
          <strong className={classes.chartInsightValue}>
            {formatCompactNumber(totalInitiated)}
          </strong>
        </div>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Onboarded</span>
          <strong className={classes.chartInsightValue}>
            {formatCompactNumber(totalOnboarded)}
          </strong>
        </div>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Conversion</span>
          <strong className={classes.chartInsightValue}>{conversionRate}%</strong>
        </div>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Strongest Window</span>
          <strong className={classes.chartInsightValue}>
            {strongestWindow?.label || "-"}:{" "}
            {formatCompactNumber(strongestWindow?.onboarded || 0)}
          </strong>
        </div>
      </div>

      <div className={`${classes.chartLegend} ${classes.chartLegendCompact}`}>
        <span className={classes.chartLegendItem}>
          <span
            className={classes.chartLegendDot}
            style={{ color: "#0e7490", background: "#0e7490" }}
          />
          Initiated
        </span>
        <span className={classes.chartLegendItem}>
          <span
            className={`${classes.chartLegendDot} ${classes.chartLegendLine}`}
            style={{ color: "#16a34a", background: "#ffffff" }}
          />
          Onboarded
        </span>
      </div>

      <div className={classes.chartContainer}>
        {activeSeries.some(
          (item) => item.initiated > 0 || item.onboarded > 0
        ) ? (
          <Bar data={chartData} options={options} />
        ) : (
          <div className={classes.chartEmptyState}>
            No onboarding activity available for the selected period.
          </div>
        )}
      </div>
    </section>
  );
};

export default VendorChart;
